import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Clock, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { openCookiePreferences } from "@/components/CookieConsent";

const CONSENT_TEXT =
  "Elolvastam és elfogadom az Adatkezelési tájékoztatót, és hozzájárulok ahhoz, hogy a megadott adataimat a Lakrosz Autószerviz a kapcsolatfelvétel céljából kezelje.";

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!consent) {
      setStatus("error");
      setErrorMsg("Az adatkezelési tájékoztató elfogadása kötelező.");
      return;
    }
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, consent: true, consentText: CONSENT_TEXT }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || "Sikertelen küldés");
      }
      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Ismeretlen hiba");
    }
  }

  return (
    <section id="contact" className="relative py-32 px-6 lg:px-10 overflow-hidden">
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{ background: "var(--gradient-radial)" }}
      />

      <div className="relative max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
            ▸ Kapcsolat
          </div>
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.9]">
            Várunk szeretettel <br />
            <span className="gradient-text">Egerben!</span>
          </h2>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
            Írj nekünk üzenetet, vagy hívj minket telefonon – munkanapokon általában néhány órán belül visszajelzünk, hogy mikor tudunk időpontot adni, és nagyjából mire számíts árban. Ha sürgős a hiba, jelezd a megjegyzésben: igyekszünk minél hamarabb beilleszteni.
          </p>
        </motion.div>

        {/* Quick contact tiles */}
        <div className="grid md:grid-cols-2 gap-4 mb-10 max-w-2xl mx-auto">
          {[
            {
              icon: MapPin,
              label: "Cím",
              value: "3300 Eger, Vécseyvölgy utca 99.",
              href: "https://www.google.com/maps?q=47.911611,20.396861",
            },
            {
              icon: Phone,
              label: "Telefon",
              value: "+36 30 478 7619",
              href: "tel:+36304787619",
            },
          ].map((c, i) => (
            <motion.a
              key={c.label}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              whileHover={{ y: -6 }}
              className="group p-8 rounded-2xl bg-surface border border-border hover:border-primary/60 hover:bg-surface-elevated transition-all"
            >
              <c.icon className="w-7 h-7 text-primary mb-4 group-hover:scale-110 group-hover:rotate-6 transition-transform" />
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {c.label}
              </div>
              <div className="text-lg font-semibold">{c.value}</div>
            </motion.a>
          ))}
        </div>

        {/* Form + Map grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* FORM */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative p-8 md:p-10 rounded-3xl bg-surface border border-border"
          >
            <div className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />

            <h3 className="font-display text-3xl md:text-4xl mb-2">
              Küldj <span className="gradient-text">üzenetet</span>
            </h3>
            <p className="text-sm text-muted-foreground mb-8">
              Töltsd ki az űrlapot – írd le röviden az autó típusát, évjáratát és a tapasztalt hibát, hogy már az első visszahíváskor érdemben tudjunk segíteni.
            </p>

            <div className="space-y-4">
              <FloatingField
                label="Név"
                name="name"
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                required
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <FloatingField
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={(v) => setForm({ ...form, email: v })}
                  required
                />
                <FloatingField
                  label="Telefonszám"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={(v) => setForm({ ...form, phone: v })}
                  required
                />
              </div>
              <FloatingField
                label="Üzenet"
                name="message"
                value={form.message}
                onChange={(v) => setForm({ ...form, message: v })}
                required
                textarea
              />
            </div>

            {/* GDPR consent */}
            <label className="mt-6 flex items-start gap-3 cursor-pointer select-none group">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
                className="mt-1 w-4 h-4 accent-[oklch(0.92_0.20_105)] cursor-pointer shrink-0"
                aria-describedby="consent-help"
              />
              <span id="consent-help" className="text-xs text-muted-foreground leading-relaxed">
                Elolvastam és elfogadom az{" "}
                <Link
                  to="/adatkezelesi-tajekoztato"
                  target="_blank"
                  className="text-primary underline hover:text-primary-glow"
                >
                  Adatkezelési tájékoztatót
                </Link>
                , és hozzájárulok ahhoz, hogy a megadott adataimat a Lakrosz Autószerviz a
                kapcsolatfelvétel céljából kezelje. <span className="text-primary">*</span>
              </span>
            </label>

            <motion.button
              type="submit"
              disabled={status === "loading" || !consent}
              whileHover={!consent ? undefined : { scale: 1.02 }}
              whileTap={!consent ? undefined : { scale: 0.98 }}
              className="mt-5 w-full inline-flex items-center justify-center gap-3 px-7 py-4 rounded-full bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold disabled:opacity-50 disabled:cursor-not-allowed glow"
            >
              {status === "loading" ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Küldés...
                </>
              ) : status === "success" ? (
                <>
                  <CheckCircle2 className="w-5 h-5" /> Elküldve!
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" /> Üzenet küldése
                </>
              )}
            </motion.button>

            {status === "success" && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 text-sm text-primary text-center"
              >
                Köszönjük! Megkaptuk az üzeneted, hamarosan jelentkezünk.
              </motion.div>
            )}
            {status === "error" && (
              <div className="mt-4 text-sm text-destructive text-center">
                Hiba történt: {errorMsg}. Kérlek próbáld újra, vagy hívj minket.
              </div>
            )}
          </motion.form>

          {/* MAP */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl overflow-hidden border border-border bg-surface min-h-[420px] relative"
          >
            <iframe
              title="Lakrosz Autószerviz — 3300 Eger, Vécseyvölgy utca 99."
              src="https://www.openstreetmap.org/export/embed.html?bbox=20.3878%2C47.9071%2C20.4060%2C47.9161&layer=mapnik&marker=47.911611%2C20.396861"
              className="w-full h-full min-h-[420px]"
              loading="lazy"
            />
            <a
              href="https://www.google.com/maps?q=47.911611,20.396861"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background/90 backdrop-blur border border-border hover:border-primary text-sm font-medium"
            >
              <MapPin className="w-4 h-4 text-primary" />
              Útvonal
            </a>
          </motion.div>
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Clock className="w-4 h-4 text-primary" />
          Időpont egyeztetés telefonon vagy űrlapon
        </div>
      </div>
    </section>
  );
}

function FloatingField({
  label,
  name,
  value,
  onChange,
  type = "text",
  required,
  textarea,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  textarea?: boolean;
}) {
  const [focused, setFocused] = useState(false);
  const active = focused || value.length > 0;
  const Tag: any = textarea ? "textarea" : "input";

  return (
    <div className="relative">
      <Tag
        id={name}
        name={name}
        type={textarea ? undefined : type}
        rows={textarea ? 5 : undefined}
        value={value}
        required={required}
        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className={`peer w-full bg-background/60 border rounded-xl px-4 ${
          textarea ? "pt-6 pb-3" : "pt-6 pb-2"
        } text-foreground outline-none transition-all resize-none ${
          focused ? "border-primary shadow-[0_0_0_3px_oklch(0.92_0.20_105/0.15)]" : "border-border"
        }`}
      />
      <label
        htmlFor={name}
        className={`absolute left-4 transition-all pointer-events-none ${
          active
            ? "top-1.5 text-[10px] uppercase tracking-widest text-primary"
            : "top-4 text-sm text-muted-foreground"
        }`}
      >
        {label}
      </label>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border py-12 px-6 lg:px-10 relative z-10">
      <div className="max-w-7xl mx-auto flex flex-col gap-8">
        <div className="grid gap-8 md:grid-cols-3">
          <div>
            <div className="font-display text-2xl tracking-wider">
              LAKROSZ<span className="text-primary">.</span> AUTÓSZERVIZ
            </div>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Márkafüggetlen autó- és motorkerékpár-szerviz Egerben.
            </p>
          </div>

          <div className="text-sm text-muted-foreground space-y-1">
            <div className="text-xs uppercase tracking-widest text-primary mb-2">Kapcsolat</div>
            <div>3300 Eger, Vécseyvölgy utca 99.</div>
            <div>
              <a href="tel:+36304787619" className="hover:text-primary transition-colors">
                +36 30 478 7619
              </a>
            </div>
            <div>
              E-mail:{" "}
              <a href="mailto:kispolgi170@gmail.com" className="hover:text-primary transition-colors">
                kispolgi170@gmail.com
              </a>
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-primary mb-2">
              Jogi információk
            </div>
            <nav className="flex flex-col gap-1.5 text-sm">
              <Link
                to="/impresszum"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Impresszum
              </Link>
              <Link
                to="/adatkezelesi-tajekoztato"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Adatkezelési tájékoztató
              </Link>
              <Link
                to="/cookie-tajekoztato"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Cookie tájékoztató
              </Link>
              <button
                type="button"
                onClick={() => openCookiePreferences()}
                className="text-left text-muted-foreground hover:text-primary transition-colors"
              >
                Cookie beállítások
              </button>
              <Link
                to="/panaszkezeles"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Panaszkezelés
              </Link>
              <Link
                to="/szolgaltatasi-feltetelek"
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Szolgáltatási feltételek
              </Link>
            </nav>
          </div>
        </div>

        <div className="text-xs text-muted-foreground border-t border-border pt-5">
          © {new Date().getFullYear()} Lakrosz Autószerviz — Minden jog fenntartva.
        </div>
      </div>
    </footer>
  );
}
